import React, { useMemo, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { UseKpiRefresh } from "../../hooks/kpi";

// Soft UI Components
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftButton from "../SoftButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Dialog, Divider, useTheme } from "@mui/material";
import SwitchModal from "../AppSwitch/SwitchModal";
import popNotification from "../Modals/popNotification";

// API services
import { SET_GROUP_KPI_ACCESS } from "../../api/kpi";
import { SET_KPI_PREFERENCES } from "../../api/preference";

const metricScoreData = [
  {
    group: "Default score entry mode",
    title: "Total Score Entry Mode",
    content: "This mode is useful when you want to update the metric to an exact or total value",
    key: "isTotalScore",
    isCheck: false
  },
  {
    group: "Default score entry mode",
    title: "Incremental Score Entry Mode",
    content: "This mode is beneficial when you want to track changes over time. For instance, if you want to add or deduct revenue for a specific event or transaction",
    key: "isIncremental",
    isCheck: false
  },
  {
    group: "Scoring Access",
    title: "Admin-only score entry",
    content: "Switching off this option prevents metric input from Players",
    key: "isAdminAccess",
    isCheck: false
  }
];

const SCORE_ENTRY_MODE = {
  ALL: "All",
  TOTAL_SCORE: "TotalScore",
  INCREMENTAL: "Incremental",
};

const MetricScoreConfig = ({
  workspaceId,
  kpiId,
  data,
  openConfig,
  handleClose,
  handleOnSuccess
}) => {
  const theme = useTheme();
  const [helperText, setHelperText] = useState(null);
  const [localSettings, setLocalSettings] = useState(null);
  const refreshKpis = UseKpiRefresh();

  useEffect(() => {
    if (localSettings) return;

    const settings = metricScoreData
      .map(x => x.key)
      .reduce((list, key) => ({
        ...list, [key]: data[key]
      }), {});

    setLocalSettings(settings);
  }, [localSettings, data]);

  const {
    primary: { main: primaryColor },
  } = theme.palette;
  const isOpen = useMemo(() => openConfig, [openConfig]);

  // Rewritten handleMetricConfigSuccess to transfer data
  const handleMetricConfigSuccess = ({
    kpiId: updatedKpiId,
    scoreEntryMode,
    isAdminAccess,
  }) => {
    // Update local state with the new settings
    setLocalSettings(prev => ({
      ...prev,
      isAdminAccess,
      isIncremental: [
        SCORE_ENTRY_MODE.INCREMENTAL,
        SCORE_ENTRY_MODE.ALL,
      ].includes(scoreEntryMode),
      isTotalScore: [
        SCORE_ENTRY_MODE.TOTAL_SCORE,
        SCORE_ENTRY_MODE.ALL,
      ].includes(scoreEntryMode),
    }));

    // Refresh KPIs
    const payload = {
      isLoading: false,
    };
    refreshKpis(payload);

    // Call parent's onSuccess if provided, transfer all relevant data
    handleOnSuccess?.({
      kpiId: updatedKpiId,
      scoreEntryMode,
      isAdminAccess,
      // Optionally transfer the new local settings for further use
      localSettings: {
        isAdminAccess,
        isIncremental: [
          SCORE_ENTRY_MODE.INCREMENTAL,
          SCORE_ENTRY_MODE.ALL,
        ].includes(scoreEntryMode),
        isTotalScore: [
          SCORE_ENTRY_MODE.TOTAL_SCORE,
          SCORE_ENTRY_MODE.ALL,
        ].includes(scoreEntryMode),
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const scoreEntryMode = () => {
      if (localSettings.isTotalScore && localSettings.isIncremental) {
        return SCORE_ENTRY_MODE.ALL;
      } else if (localSettings.isTotalScore) {
        return SCORE_ENTRY_MODE.TOTAL_SCORE;
      } else if (localSettings.isIncremental) {
        return SCORE_ENTRY_MODE.INCREMENTAL;
      } else {
        return "None";
      }
    }

    const payloadRestriction = {
      kpiId,
      isAdminAccess: localSettings.isAdminAccess
    }

    const scoreEntryPayload = {
      kpiId,
      groupId: workspaceId,
      entryMode: scoreEntryMode()
    }

    if (scoreEntryPayload.entryMode === "None") {
      setHelperText("Please select a Default score entry mode.");
      return;
    }

    try {
      await SET_GROUP_KPI_ACCESS(workspaceId, payloadRestriction);
      await SET_KPI_PREFERENCES(scoreEntryPayload);

      handleMetricConfigSuccess({
        kpiId,
        scoreEntryMode: scoreEntryPayload.entryMode,
        isAdminAccess: localSettings.isAdminAccess
      });

      popNotification({
        title: "Success",
        text: "Your metric score configuration was saved."
      });
    } catch (error) {
      toast.error("There was an error saving data.");
    }

    handleClose?.();
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      sx={{
        margin: "auto",
        width: { xs: "100vw", sm: "60vw" },
        ".MuiPaper-root": {
          borderRadius: "8px",
          width: "100%",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
          boxShadow:
            "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
          backdropFilter: "blur(18px) saturate(160%)",
          border: "1px solid rgba(255,255,255,0.6)",
          padding: { mini: "1rem", md: "1.5rem" },
        },
      }}
    >
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <SoftTypography variant="h4" sx={{ color: "#0F172A" }}>
          Metric Score Configuration
        </SoftTypography>
        <SoftButton
          variant="outlined"
          onClick={handleClose}
          sx={{
            "& svg": { fontSize: "1.25rem !important" },
            borderRadius: "50%",
            minWidth: "max-content",
            minHeight: "max-content",
            padding: "0.25rem",
            color: "#475467",
          }}
        >
          <CloseRoundedIcon />
        </SoftButton>
      </SoftBox>
      <Divider
        sx={{
          backgroundImage: `linear-gradient(to right, rgba(52, 71, 103, 0), ${primaryColor}, rgba(52, 71, 103, 0)) !important`,
          "&:not([size])": {
            height: "2px",
          },
        }}
      />
      <form onSubmit={handleSubmit}>
        <SoftBox marginTop="1.5rem" marginBottom="0.5rem">
          {[...new Set(metricScoreData.map(x => x.group))]
            .map((group) => (
            <div key={crypto.randomUUID()}>
              <SoftTypography
                variant="body2"
                mb="0.5rem"
                sx={{
                  color: "#475467",
                }}
              >
                {group}
              </SoftTypography>

              {metricScoreData.map((item) => {
                if (item.group === group) {
                  return <SoftBox
                    key={crypto.randomUUID()}
                    marginBottom="1rem"
                    borderRadius="14px"
                    sx={{
                      padding: "1rem",
                      background: "rgba(15,23,42,0.03)",
                      border: "1px solid rgba(15,23,42,0.10)",
                    }}
                  >
                    <SoftBox
                      marginBottom="0.25rem"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <SoftTypography variant="h6" sx={{ color: "#0F172A" }}>
                        {item.title}
                      </SoftTypography>

                      <SwitchModal
                        id={item.key}
                        name={item.key}
                        defaultChecked={localSettings?.[item?.key]}
                        onChange={(e) => {
                          const key = e.target.name;
                          const update = {};

                          if (key === "isAdminAccess") {
                            update[key] = e.target.checked;
                          }

                          if (key === "isTotalScore") {
                            update[key] = e.target.checked;
                            update.isIncremental = !e.target.checked;
                          } else if (key === "isIncremental") {
                            update[key] = e.target.checked;
                            update.isTotalScore = !e.target.checked;
                          }

                          setLocalSettings(prev => ({ ...prev, ...update }));
                        }}
                      />
                    </SoftBox>
                    <SoftTypography
                      variant="button"
                      sx={{ color: "#475467", fontWeight: 400 }}
                    >
                      {item.content}
                    </SoftTypography>
                  </SoftBox>
                }
              })}
            </div>
          ))}

          <SoftBox sx={{ color: "#ea0606", fontSize: "12px" }}>
            {helperText}
          </SoftBox>
        </SoftBox>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginTop="2.5rem"
        >
          <SoftButton
            size="large"
            variant="gradient"
            sx={{ textTransform: "none" }}
            color="primary"
            type="submit"
          >
            Update
          </SoftButton>
        </SoftBox>
      </form>
    </Dialog>
  );
};

export default MetricScoreConfig;
